import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, Search, Filter, ShieldAlert, X } from "lucide-react";
import { Field, Input, Select, GoldButton, GhostButton, PageHeader, EmptyState } from "@/components/Ui";
import { fmtDateTime } from "@/lib/time";
import { caseLabel, clientLabel } from "@/lib/labels";
import HistoryDrawer from "@/components/HistoryDrawer";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const ENTITY_OPTIONS = [
    { value: "", label: "All entities" },
    { value: "case", label: "Litigation" },
    { value: "client", label: "Client" },
    { value: "hearing", label: "Hearing" },
    { value: "invoice", label: "Invoice" },
    { value: "communication", label: "Communication" },
    { value: "task", label: "Task" },
];

const KIND_OPTIONS = [
    { value: "", label: "All actions" },
    { value: "change", label: "Edit / Change" },
    { value: "impersonate", label: "Impersonate" },
];

export default function AuditLog() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [cases, setCases] = useState([]);
    const [users, setUsers] = useState([]);
    const [historyFor, setHistoryFor] = useState(null);
    const today = new Date().toISOString().slice(0, 10);
    const defaultFrom = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
    const [filters, setFilters] = useState({
        entity: "",
        kind: "change",
        client_id: "",
        case_id: "",
        user_id: "",
        date_from: defaultFrom,
        date_to: today,
        q: "",
    });

    // Bootstrap reference data — clients, cases, users
    useEffect(() => {
        Promise.all([
            api.get("/clients").catch(() => ({ data: [] })),
            api.get("/cases").catch(() => ({ data: [] })),
            api.get("/users").catch(() => ({ data: [] })),
        ]).then(([cl, cs, st]) => {
            setClients(cl.data || []);
            setCases(cs.data || []);
            setUsers(st.data || []);
        });
    }, []);

    // Cases visible in the litigation picker — narrow by client when selected
    const filteredCases = useMemo(() => {
        if (!filters.client_id) return cases;
        return cases.filter(c => c.client_id === filters.client_id);
    }, [cases, filters.client_id]);

    const loadAudit = async () => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            // map case selection to entity_id when entity not explicitly set
            if (filters.case_id) { params.entity = "case"; params.entity_id = filters.case_id; }
            const { data } = await api.get("/audit/tenant", { params });
            setItems(data || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { loadAudit(); /* eslint-disable-next-line */ }, [filters]);

    // ---- Cross-filter linkage (two-way sync) ----
    const onPickClient = (clientId) => {
        // Reset case if it no longer belongs to this client
        const caseOk = !filters.case_id || cases.find(c => c.id === filters.case_id)?.client_id === clientId;
        setFilters(f => ({ ...f, client_id: clientId, case_id: caseOk ? f.case_id : "" }));
    };
    const onPickCase = (caseId) => {
        const c = cases.find(x => x.id === caseId);
        // Auto-fill client when picking a case (two-way sync)
        setFilters(f => ({ ...f, case_id: caseId, client_id: c?.client_id || f.client_id }));
    };

    const clearFilters = () => setFilters({
        entity: "", kind: "change", client_id: "", case_id: "", user_id: "",
        date_from: defaultFrom, date_to: today, q: "",
    });

    const exportCsv = async () => {
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
            if (filters.case_id) { params.set("entity", "case"); params.set("entity_id", filters.case_id); }
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/audit/tenant/export.csv?${params.toString()}`;
            // axios with blob keeps the auth cookie
            const res = await api.get(`/audit/tenant/export.csv?${params.toString()}`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
            a.download = `audit-log-${stamp}.csv`;
            a.click();
            URL.revokeObjectURL(a.href);
            toast.success("Audit log exported · open in Excel");
        } catch (e) { toast.error(apiError(e)); }
    };

    const clientOptions = useMemo(() => [
        { value: "", label: "All clients" },
        ...clients.map(c => ({ value: c.id, label: clientLabel(c) })),
    ], [clients]);

    const caseOptions = useMemo(() => [
        { value: "", label: "All litigations" },
        ...filteredCases.map(c => ({ value: c.id, label: caseLabel(c) })),
    ], [filteredCases]);

    const userOptions = useMemo(() => [
        { value: "", label: "All users" },
        ...users.map(u => ({ value: u.id, label: `${u.name || u.email} · ${u.role}` })),
    ], [users]);

    const activeCount = Object.entries(filters).filter(([k, v]) =>
        v && !(k === "kind" && v === "change") && !(k === "date_from" && v === defaultFrom) && !(k === "date_to" && v === today)
    ).length;

    const tableCtrl = useTableControls(items, {
        storageKey: "audit-log",
        accessors: {
            when: (a) => a.created_at,
            user: (a) => a.user_email || a.actor_email || "",
            action: (a) => a.kind || a.method || "",
            reference: (a) => `${a.entity_label || ""} ${a.court_case_number || ""} ${a.entity_title || ""}`.trim(),
            changes: (a) => (a.changes || []).map(c => `${c.field}:${c.old}→${c.new}`).join(" "),
            remarks: (a) => a.reason || "",
        },
    });

    return (
        <>
            <PageHeader
                title="Audit log"
                subtitle="Every change to a case, client, invoice, hearing or message — with the mandatory remark and field-level diff. Built for compliance reviews and partner sign-off."
                action={
                    <div className="flex gap-2">
                        {activeCount > 0 && (
                            <GhostButton onClick={clearFilters} data-testid="audit-clear-filters">
                                <X className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={1.5}/> Clear filters
                            </GhostButton>
                        )}
                        <GoldButton onClick={exportCsv} data-testid="audit-export-btn">
                            <Download className="w-4 h-4 inline mr-2" strokeWidth={2}/> Export to Excel
                        </GoldButton>
                    </div>
                }
            />

            {/* Filter bar */}
            <div className="border border-border bg-muted p-4 mb-6">
                <div className="flex items-center gap-2 mb-3">
                    <Filter className="w-3.5 h-3.5 text-primary" strokeWidth={1.5}/>
                    <div className="overline">Filters</div>
                    {loading && <div className="text-[10px] text-muted-foreground">loading…</div>}
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Field label="Client">
                        <Select value={filters.client_id} onChange={e => onPickClient(e.target.value)} options={clientOptions} data-testid="audit-filter-client"/>
                    </Field>
                    <Field label="Case (internal # · court #)">
                        <Select value={filters.case_id} onChange={e => onPickCase(e.target.value)} options={caseOptions} data-testid="audit-filter-case"/>
                    </Field>
                    <Field label="Entity">
                        <Select value={filters.entity} onChange={e => setFilters(f => ({ ...f, entity: e.target.value }))} options={ENTITY_OPTIONS} data-testid="audit-filter-entity"/>
                    </Field>
                    <Field label="Action">
                        <Select value={filters.kind} onChange={e => setFilters(f => ({ ...f, kind: e.target.value }))} options={KIND_OPTIONS} data-testid="audit-filter-kind"/>
                    </Field>
                    <Field label="User">
                        <Select value={filters.user_id} onChange={e => setFilters(f => ({ ...f, user_id: e.target.value }))} options={userOptions} data-testid="audit-filter-user"/>
                    </Field>
                    <Field label="From">
                        <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="audit-filter-from"/>
                    </Field>
                    <Field label="To">
                        <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="audit-filter-to"/>
                    </Field>
                    <Field label="Remarks contain…">
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5}/>
                            <Input className="pl-8" placeholder="e.g. typo, corrected, refund" value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} data-testid="audit-filter-q"/>
                        </div>
                    </Field>
                </div>
            </div>

            {/* Results */}
            <div className="text-xs text-muted-foreground mb-3" data-testid="audit-count">{items.length} record{items.length !== 1 ? "s" : ""}</div>
            {items.length === 0 ? (
                <EmptyState title="No audit entries match these filters" subtitle="Widen the date range or clear filters to see more activity." action={activeCount > 0 ? <GhostButton onClick={clearFilters}>Clear filters</GhostButton> : null}/>
            ) : (
                <div className="border border-border overflow-x-auto">
                    <table className="min-w-[900px] w-full text-sm">
                        <thead className="bg-muted border-b border-border">
                            <tr className="text-left text-muted-foreground">
                                <SortableTh field="when" ctrl={tableCtrl}>When (IST)</SortableTh>
                                <SortableTh field="user" ctrl={tableCtrl}>User</SortableTh>
                                <SortableTh field="action" ctrl={tableCtrl}>Action</SortableTh>
                                <SortableTh field="entity" ctrl={tableCtrl}>Entity</SortableTh>
                                <SortableTh field="reference" ctrl={tableCtrl}>Reference</SortableTh>
                                <SortableTh field="changes" ctrl={tableCtrl}>Changes</SortableTh>
                                <SortableTh field="remarks" ctrl={tableCtrl}>Remarks</SortableTh>
                                <th className="px-4 py-3 text-xs uppercase tracking-wider font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableCtrl.rows.map(a => (
                                <tr key={a.id} className="border-b border-border hover:bg-muted/50" data-testid={`audit-row-${a.id}`}>
                                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground whitespace-nowrap">{fmtDateTime(a.created_at)}</td>
                                    <td className="px-4 py-3 text-xs">{a.user_email || a.actor_email || "—"}</td>
                                    <td className="px-4 py-3 text-xs uppercase tracking-wider text-primary">{a.kind || a.method || "—"}</td>
                                    <td className="px-4 py-3 text-xs">{a.entity || "—"}</td>
                                    <td className="px-4 py-3 text-xs">
                                        {a.entity_label && <div className="font-mono text-primary">{a.entity_label}</div>}
                                        {a.court_case_number && <div className="font-mono text-[10px] text-muted-foreground">{a.court_case_number}</div>}
                                        {a.entity_title && <div className="text-[10px] text-muted-foreground truncate max-w-[220px]">{a.entity_title}</div>}
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono">
                                        {(a.changes || []).slice(0, 2).map((c, i) => (
                                            <div key={`${c.field}-${i}`} className="text-[10px] truncate max-w-[260px]">
                                                <span className="text-muted-foreground">{c.field}: </span>
                                                <span className="text-red-400/80">{String(c.old ?? "—").slice(0, 20)}</span>
                                                <span className="text-muted-foreground"> → </span>
                                                <span className="text-emerald-400/80">{String(c.new ?? "—").slice(0, 20)}</span>
                                            </div>
                                        ))}
                                        {(a.changes || []).length > 2 && <div className="text-[10px] text-muted-foreground">+{a.changes.length - 2} more</div>}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-primary italic max-w-[280px]">{a.reason ? `“${a.reason}”` : <span className="text-muted-foreground not-italic">—</span>}</td>
                                    <td className="px-4 py-3 text-right">
                                        {a.entity && a.entity_id && (
                                            <button onClick={() => setHistoryFor({ entity: a.entity, id: a.entity_id, title: `${a.entity_label || ""} ${a.court_case_number ? "· " + a.court_case_number : ""}`.trim() })}
                                                data-testid={`audit-row-history-${a.id}`}
                                                className="text-xs text-muted-foreground hover:text-primary">View →</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <HistoryDrawer open={!!historyFor} onClose={() => setHistoryFor(null)} entity={historyFor?.entity} entityId={historyFor?.id} title={historyFor?.title}/>

            {/* Decorative footer */}
            <div className="mt-8 text-[10px] text-muted-foreground flex items-center gap-2 justify-end">
                <ShieldAlert className="w-3 h-3" strokeWidth={1.5}/> Audit data is tamper-evident · captured server-side at write time.
            </div>
        </>
    );
}
